import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    AlertTriangle,
    ArrowRight,
    Banknote,
    BarChart3,
    Calendar,
    CheckCircle2,
    ChevronDown,
    ChevronRight,
    ChevronUp,
    CreditCard,
    FileWarning,
    FolderOpen,
    Handshake,
    Loader2,
    Mail,
    MessageCircle,
    RefreshCw,
    Shield,
    Smartphone,
    TrendingUp,
    UserPlus,
    Users,
    X,
} from "lucide-react";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Pie,
    PieChart,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import ChartBox from "./charts/ChartBox";
import { muvekkilListeOzet } from "../lib/api";

const LS_EXPANDED = "muvekkilTakipExpanded";

const AYLAR = [
    { value: 0, label: "Tüm yıl" },
    { value: 1, label: "Oca" },
    { value: 2, label: "Şub" },
    { value: 3, label: "Mar" },
    { value: 4, label: "Nis" },
    { value: 5, label: "May" },
    { value: 6, label: "Haz" },
    { value: 7, label: "Tem" },
    { value: 8, label: "Ağu" },
    { value: 9, label: "Eyl" },
    { value: 10, label: "Eki" },
    { value: 11, label: "Kas" },
    { value: 12, label: "Ara" },
];

const YILLAR = [2023, 2024, 2025, 2026, 2027];

const UYARI_STIL = {
    warning: "border-amber-500/35 bg-amber-500/10 text-amber-100",
    danger: "border-rose-500/35 bg-rose-500/10 text-rose-100",
    info: "border-sky-500/35 bg-sky-500/10 text-sky-100",
};

const UYARI_IKON = {
    file: FileWarning,
    shield: Shield,
    smartphone: Smartphone,
    mail: Mail,
    calendar: Calendar,
    alert: AlertTriangle,
};

const KPI_THEME = {
    violet: { border: "border-violet-500/40", bg: "bg-violet-500/10", text: "text-violet-200" },
    sky: { border: "border-sky-500/40", bg: "bg-sky-500/10", text: "text-sky-200" },
    gold: { border: "border-[#D4AF37]/45", bg: "bg-[#D4AF37]/10", text: "text-[#F3E5AB]" },
    amber: { border: "border-amber-500/40", bg: "bg-amber-500/10", text: "text-amber-200" },
    rose: { border: "border-rose-500/40", bg: "bg-rose-500/10", text: "text-rose-200" },
    emerald: { border: "border-emerald-500/40", bg: "bg-emerald-500/10", text: "text-emerald-200" },
};

function fmt(n) {
    return new Intl.NumberFormat("tr-TR", {
        style: "currency",
        currency: "TRY",
        maximumFractionDigits: 0,
    }).format(n || 0);
}

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("tr-TR", {
            day: "2-digit",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

function KpiChip({ label, value, accent = "violet", active, onClick, title }) {
    const t = KPI_THEME[accent] || KPI_THEME.violet;
    const Tag = onClick ? "button" : "div";
    return (
        <Tag
            type={onClick ? "button" : undefined}
            onClick={onClick}
            title={title || label}
            className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 min-w-0 transition ${t.border} ${t.bg} ${
                onClick ? "hover:brightness-110 cursor-pointer" : ""
            } ${active ? "ring-1 ring-white/30" : ""}`}
        >
            <span className="text-[9px] uppercase tracking-wide text-slate-500 truncate max-w-[72px]">
                {label}
            </span>
            <span className={`text-sm font-semibold tabular-nums leading-none ${t.text}`}>
                {value ?? "—"}
            </span>
            {onClick ? (
                <ChevronRight className="h-2.5 w-2.5 text-slate-600 shrink-0 opacity-50" />
            ) : null}
        </Tag>
    );
}

function MuvekkilUyariListesi({ kayitlar, showTutar = false, compact = false }) {
    const [expanded, setExpanded] = useState(false);
    if (!kayitlar?.length) return null;

    const PREVIEW = compact ? 5 : 8;
    const hasMore = kayitlar.length > PREVIEW;
    const visible =
        expanded || !hasMore ? kayitlar : kayitlar.slice(0, PREVIEW);

    return (
        <div className="mt-2 border-t border-white/10 pt-2">
            <div className="overflow-x-auto rounded border border-white/5 bg-black/20">
                <table className="w-full min-w-[360px] text-left text-[10px]">
                    <thead>
                        <tr className="text-slate-500 uppercase tracking-wider text-[9px] border-b border-white/10">
                            <th className="px-2 py-1 font-semibold">Dosya No</th>
                            <th className="px-2 py-1 font-semibold">TC / Vergi</th>
                            <th className="px-2 py-1 font-semibold">Ad Soyad</th>
                            {showTutar ? (
                                <th className="px-2 py-1 font-semibold text-right">Tutar</th>
                            ) : null}
                        </tr>
                    </thead>
                    <tbody>
                        {visible.map((m) => (
                            <tr
                                key={m.id}
                                className="border-b border-white/5 last:border-0 hover:bg-white/[0.03]"
                            >
                                <td className="px-2 py-1 font-mono text-[#F3E5AB] whitespace-nowrap">
                                    {m.dosyalar?.length > 1 ? (
                                        <span title={m.dosyalar.join(", ")} className="cursor-help">
                                            {m.dosya_no}{" "}
                                            <span className="text-slate-500">+{m.dosyalar.length - 1}</span>
                                        </span>
                                    ) : (
                                        m.dosya_no || "—"
                                    )}
                                </td>
                                <td className="px-2 py-1 font-mono text-slate-400 whitespace-nowrap">
                                    {m.tc_kimlik || "—"}
                                </td>
                                <td className="px-2 py-1">
                                    <Link
                                        to={`/e/muvekkiller/${m.id}`}
                                        className="text-slate-200 hover:text-[#F3E5AB] hover:underline"
                                    >
                                        {m.ad_soyad}
                                    </Link>
                                </td>
                                {showTutar ? (
                                    <td className="px-2 py-1 text-right font-mono text-rose-300 whitespace-nowrap">
                                        {fmt(m.tutar)}
                                    </td>
                                ) : null}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {hasMore ? (
                <button
                    type="button"
                    onClick={() => setExpanded((v) => !v)}
                    className="mt-1 inline-flex items-center gap-0.5 text-[10px] text-slate-400 hover:text-slate-200"
                >
                    {expanded ? (
                        <>
                            <ChevronUp className="h-3 w-3" /> Daha az
                        </>
                    ) : (
                        <>
                            <ChevronDown className="h-3 w-3" /> +{kayitlar.length - PREVIEW} kayıt daha
                        </>
                    )}
                </button>
            ) : null}
        </div>
    );
}

function MuvekkilUyariKart({ uyari, open, onToggle }) {
    const Icon = UYARI_IKON[uyari.ikon] || AlertTriangle;
    const showTutar = uyari.kod === "hakedis_bekleyen";
    const kayitSayisi = uyari.kayitlar?.length || 0;

    return (
        <div
            className={`rounded-md border text-[11px] ${UYARI_STIL[uyari.seviye] || UYARI_STIL.info}`}
        >
            <button
                type="button"
                onClick={onToggle}
                className="w-full flex items-center gap-2 px-2.5 py-1.5 text-left hover:bg-white/[0.03] transition rounded-md"
            >
                <Icon className="h-3.5 w-3.5 shrink-0" />
                <span className="font-medium flex-1 min-w-0 truncate">{uyari.baslik}</span>
                {kayitSayisi > 0 ? (
                    <span className="text-[10px] font-mono opacity-70 shrink-0">
                        {kayitSayisi} kayıt
                    </span>
                ) : null}
                {open ? (
                    <ChevronUp className="h-3.5 w-3.5 shrink-0 opacity-60" />
                ) : (
                    <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
                )}
            </button>
            {open ? (
                <div className="px-2.5 pb-2">
                    <p className="text-[10px] text-slate-400/90 leading-snug">{uyari.aciklama}</p>
                    <MuvekkilUyariListesi
                        kayitlar={uyari.kayitlar}
                        showTutar={showTutar}
                        compact
                    />
                </div>
            ) : null}
        </div>
    );
}

function MiniPieCompact({ data, title, ready = true }) {
    const filtered = (data || []).filter((d) => d.value > 0);
    if (!filtered.length) return null;
    return (
        <div className="flex flex-col items-center">
            <div className="text-[9px] uppercase tracking-wider text-slate-500 mb-0.5">{title}</div>
            <ChartBox height={72} width={90} debounce={48} ready={ready}>
                <PieChart>
                    <Pie
                        data={filtered}
                        dataKey="value"
                        cx="50%"
                        cy="50%"
                        innerRadius={18}
                        outerRadius={32}
                        paddingAngle={2}
                    >
                        {filtered.map((entry, i) => (
                            <Cell key={i} fill={entry.color} />
                        ))}
                    </Pie>
                </PieChart>
            </ChartBox>
            <div className="flex flex-wrap justify-center gap-x-1.5 gap-y-0.5">
                {filtered.map((d) => (
                    <span key={d.name} className="text-[9px] text-slate-500 inline-flex items-center gap-0.5">
                        <span className="h-1.5 w-1.5 rounded-full" style={{ background: d.color }} />
                        {d.name} {d.value}
                    </span>
                ))}
            </div>
        </div>
    );
}

export default function MuvekkilTakipPanel({
    period,
    onPeriodChange,
    onRefresh,
    listCount,
    activeFilter,
    onFilterChange,
}) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(() => {
        try {
            return localStorage.getItem(LS_EXPANDED) === "1";
        } catch {
            return false;
        }
    });
    const [openUyari, setOpenUyari] = useState(null);
    const [sideTab, setSideTab] = useState("son");
    const [chartsReady, setChartsReady] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        const params = { yil: period.yil };
        if (period.ay) params.ay = period.ay;
        muvekkilListeOzet(params)
            .then(setData)
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [period.yil, period.ay]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        try {
            localStorage.setItem(LS_EXPANDED, expanded ? "1" : "0");
        } catch {
            /* ignore */
        }
    }, [expanded]);

    useEffect(() => {
        if (!expanded) {
            setChartsReady(false);
            return undefined;
        }
        const frame = window.requestAnimationFrame(() => setChartsReady(true));
        return () => window.cancelAnimationFrame(frame);
    }, [expanded]);

    const chartData = useMemo(() => data?.aylik || [], [data]);
    const uyarilar = useMemo(
        () => (data?.uyarilar || []).filter((u) => u.kod || u.kayitlar?.length),
        [data],
    );
    const oranlar = data?.oranlar || {};
    const genel = data?.genel || {};

    const toggleFilter = (key, value) => {
        if (!onFilterChange) return;
        if (activeFilter?.key === key && activeFilter?.value === value) {
            onFilterChange(null);
        } else {
            onFilterChange({ key, value });
        }
    };

    const isActive = (key, value) =>
        activeFilter?.key === key && activeFilter?.value === value;

    const activeFilterLabel = useMemo(() => {
        if (!activeFilter) return null;
        const map = {
            ALINDI: "Vekalet alındı",
            ALINMADI: "Vekalet eksik",
            "1": "Evet",
        };
        const labels = {
            vekalet_durumu: map[activeFilter.value] || activeFilter.value,
            acik_dosyali: "Açık dosyalı",
            portal_sifreli: "Portal şifreli",
            portal_eksik: "Portal eksik",
            kvkk_bekleyen: "KVKK bekleyen",
            whatsapp_eksik: "WhatsApp eksik",
            hakedis_bekleyen: "Hakediş bekleyen",
            iban_eksik: "IBAN eksik",
            email_eksik: "E-posta eksik",
            aracili: "Aracılı",
            onboarding_eksik: "Onboarding eksik",
        };
        return labels[activeFilter.key] || activeFilter.value;
    }, [activeFilter]);

    const filteredChart = period.ay
        ? chartData.filter((d) => d.ay === period.ay)
        : chartData;

    const kritikUyarilar = uyarilar.filter((u) =>
        ["hakedis_bekleyen", "kvkk_bekleyen", "portal_eksik", "whatsapp_eksik", "iban_eksik"].includes(
            u.kod,
        ),
    );

    return (
        <section className="rounded-lg border border-[#D4AF37]/15 bg-[#121A2F]/80 mb-2 overflow-hidden">
            {/* Toolbar — tek satır */}
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 px-3 py-1.5 border-b border-white/5 bg-[#0f1424]/60">
                <div className="flex items-center gap-1.5 min-w-0">
                    <Users className="h-3.5 w-3.5 text-[#D4AF37] shrink-0" />
                    <span className="text-sm font-medium text-white whitespace-nowrap">
                        {data?.ay_label || "Özet"} · {period.yil}
                    </span>
                    {listCount != null ? (
                        <span className="text-[10px] font-mono text-slate-500 border border-white/10 rounded px-1 py-0.5">
                            {listCount} kayıt
                        </span>
                    ) : null}
                </div>

                <div className="flex flex-wrap items-center gap-0.5 flex-1 min-w-[180px]">
                    {AYLAR.map((a) => {
                        const active = period.ay === a.value;
                        return (
                            <button
                                key={a.value}
                                type="button"
                                onClick={() => onPeriodChange({ ...period, ay: a.value })}
                                className={`px-1.5 py-0.5 rounded text-[10px] font-medium transition ${
                                    active
                                        ? "bg-[#D4AF37]/85 text-black"
                                        : "text-slate-500 hover:text-slate-300 hover:bg-white/5"
                                }`}
                            >
                                {a.label}
                            </button>
                        );
                    })}
                </div>

                <div className="flex items-center gap-1 shrink-0">
                    <select
                        value={period.yil}
                        onChange={(e) =>
                            onPeriodChange({ ...period, yil: Number(e.target.value) })
                        }
                        className="h-7 bg-[#0B0F19] border border-white/10 rounded-md px-1.5 text-[11px] text-slate-200 focus:outline-none"
                    >
                        {YILLAR.map((y) => (
                            <option key={y} value={y}>
                                {y}
                            </option>
                        ))}
                    </select>
                    <button
                        type="button"
                        onClick={() => {
                            load();
                            onRefresh?.();
                        }}
                        disabled={loading}
                        className="inline-flex items-center h-7 px-1.5 rounded-md border border-white/10 text-slate-400 hover:text-white disabled:opacity-50"
                        title="Yenile"
                    >
                        {loading ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                            <RefreshCw className="h-3.5 w-3.5" />
                        )}
                    </button>
                    <Link
                        to="/yeni/muvekkil-merkezi"
                        className="inline-flex items-center h-7 px-1.5 rounded-md border border-white/10 text-slate-400 hover:text-white"
                        title="Müvekkil Merkezi"
                    >
                        <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                    <button
                        type="button"
                        onClick={() => setExpanded((v) => !v)}
                        className={`inline-flex items-center gap-1 h-7 px-2 rounded-md border text-[10px] transition ${
                            expanded
                                ? "border-[#D4AF37]/40 bg-[#D4AF37]/15 text-[#F3E5AB]"
                                : "border-white/10 text-slate-400 hover:text-slate-200"
                        }`}
                    >
                        <BarChart3 className="h-3.5 w-3.5" />
                        Detay
                        {expanded ? (
                            <ChevronUp className="h-3 w-3" />
                        ) : (
                            <ChevronDown className="h-3 w-3" />
                        )}
                    </button>
                </div>
            </div>

            {loading && !data ? (
                <div className="flex items-center gap-2 text-slate-500 text-xs px-3 py-3">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Özet yükleniyor…
                </div>
            ) : (
                <>
                    {/* Uyarılar — accordion, varsayılan kapalı */}
                    {kritikUyarilar.length > 0 ? (
                        <div className="px-3 py-1.5 border-b border-white/5 space-y-1">
                            {kritikUyarilar.map((u, i) => (
                                <MuvekkilUyariKart
                                    key={u.kod || i}
                                    uyari={u}
                                    open={openUyari === u.kod}
                                    onToggle={() =>
                                        setOpenUyari((prev) =>
                                            prev === u.kod ? null : u.kod,
                                        )
                                    }
                                />
                            ))}
                        </div>
                    ) : null}

                    {/* KPI şeridi — kompakt */}
                    <div className="px-3 py-1.5 border-b border-white/5">
                        <div className="flex flex-wrap gap-1">
                            <KpiChip
                                label="Dönem"
                                value={data?.donem_kayit}
                                accent="gold"
                                title={`Toplam ${data?.toplam_muvekkil ?? "—"} müvekkil`}
                            />
                            <KpiChip
                                label="Vekalet ✓"
                                value={data?.vekalet_alindi}
                                accent="emerald"
                                active={isActive("vekalet_durumu", "ALINDI")}
                                onClick={() => toggleFilter("vekalet_durumu", "ALINDI")}
                                title={`%${oranlar.vekalet ?? 0} · ${data?.vekalet_alinmadi ?? 0} eksik`}
                            />
                            <KpiChip
                                label="Vekalet —"
                                value={data?.vekalet_alinmadi}
                                accent="amber"
                                active={isActive("vekalet_durumu", "ALINMADI")}
                                onClick={() => toggleFilter("vekalet_durumu", "ALINMADI")}
                            />
                            <KpiChip
                                label="Açık dosya"
                                value={data?.acik_dosyali_muvekkil}
                                accent="sky"
                                active={isActive("acik_dosyali", "1")}
                                onClick={() => toggleFilter("acik_dosyali", "1")}
                                title={`${data?.acik_dosya_sayisi ?? 0} dosya`}
                            />
                            <KpiChip
                                label="Portal ✓"
                                value={data?.portal_sifreli}
                                accent="gold"
                                active={isActive("portal_sifreli", "1")}
                                onClick={() => toggleFilter("portal_sifreli", "1")}
                                title={`${data?.portal_giris_yapan ?? 0} giriş`}
                            />
                            <KpiChip
                                label="Portal —"
                                value={data?.portal_eksik}
                                accent="violet"
                                active={isActive("portal_eksik", "1")}
                                onClick={() => toggleFilter("portal_eksik", "1")}
                            />
                            <KpiChip
                                label="KVKK —"
                                value={data?.kvkk_bekleyen}
                                accent="rose"
                                active={isActive("kvkk_bekleyen", "1")}
                                onClick={() => toggleFilter("kvkk_bekleyen", "1")}
                            />
                            <KpiChip
                                label="WhatsApp —"
                                value={data?.whatsapp_eksik}
                                accent="amber"
                                active={isActive("whatsapp_eksik", "1")}
                                onClick={() => toggleFilter("whatsapp_eksik", "1")}
                            />
                            <KpiChip
                                label="Hakediş"
                                value={data?.hakedis_bekleyen}
                                accent="rose"
                                active={isActive("hakedis_bekleyen", "1")}
                                onClick={() => toggleFilter("hakedis_bekleyen", "1")}
                                title={fmt(data?.bekleyen_hakedis_tutar)}
                            />
                            <KpiChip
                                label="IBAN —"
                                value={data?.iban_eksik}
                                accent="amber"
                                active={isActive("iban_eksik", "1")}
                                onClick={() => toggleFilter("iban_eksik", "1")}
                            />
                        </div>
                        {activeFilterLabel ? (
                            <div className="mt-1 flex items-center gap-2 text-[10px] text-[#D4AF37]">
                                <span>Filtre: {activeFilterLabel}</span>
                                <button
                                    type="button"
                                    onClick={() => onFilterChange?.(null)}
                                    className="inline-flex items-center gap-0.5 text-slate-500 hover:text-white"
                                >
                                    <X className="h-3 w-3" />
                                    Temizle
                                </button>
                            </div>
                        ) : null}
                    </div>

                    {/* Detay — grafik + yan listeler, varsayılan kapalı */}
                    {expanded ? (
                        <div className="px-3 py-2 bg-[#0B0F19]/30 border-b border-white/5">
                            <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_180px] gap-3 items-stretch">
                                <div className="min-w-0">
                                    <div className="text-[9px] uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1">
                                        <TrendingUp className="h-3 w-3 text-[#D4AF37]" />
                                        {period.ay
                                            ? `${data?.ay_label} kayıtları`
                                            : `${period.yil} aylık trend`}
                                    </div>
                                    <ChartBox height={100} ready={chartsReady} debounce={48}>
                                        <BarChart
                                            data={filteredChart}
                                            margin={{ top: 4, right: 4, left: -22, bottom: 0 }}
                                        >
                                            <CartesianGrid
                                                strokeDasharray="3 3"
                                                stroke="#334155"
                                                vertical={false}
                                            />
                                            <XAxis
                                                dataKey="ay_label"
                                                tick={{ fill: "#64748b", fontSize: 9 }}
                                                axisLine={false}
                                                tickLine={false}
                                            />
                                            <YAxis
                                                tick={{ fill: "#64748b", fontSize: 9 }}
                                                allowDecimals={false}
                                                axisLine={false}
                                                tickLine={false}
                                                width={26}
                                            />
                                            <Tooltip
                                                contentStyle={{
                                                    background: "#0B0F19",
                                                    border: "1px solid #334155",
                                                    borderRadius: 6,
                                                    fontSize: 10,
                                                }}
                                            />
                                            <Bar
                                                dataKey="yeni_kayit"
                                                name="Yeni"
                                                fill="#D4AF37"
                                                radius={[2, 2, 0, 0]}
                                                maxBarSize={20}
                                            />
                                            <Bar
                                                dataKey="portal_aktif"
                                                name="Portal"
                                                fill="#8B5CF6"
                                                radius={[2, 2, 0, 0]}
                                                maxBarSize={20}
                                            />
                                            <Bar
                                                dataKey="whatsapp_onay"
                                                name="WhatsApp"
                                                fill="#10B981"
                                                radius={[2, 2, 0, 0]}
                                                maxBarSize={20}
                                            />
                                        </BarChart>
                                    </ChartBox>
                                </div>

                                <div className="flex flex-wrap justify-center gap-4 lg:border-x lg:border-white/5 lg:px-3">
                                    <MiniPieCompact data={data?.durum_pie} title="Vekalet" ready={chartsReady} />
                                    <MiniPieCompact data={data?.portal_pie} title="Portal" ready={chartsReady} />
                                    <MiniPieCompact data={data?.kimlik_pie} title="Kimlik" ready={chartsReady} />
                                </div>

                                <div className="min-w-0 flex flex-col">
                                    <div className="flex gap-0.5 mb-1">
                                        {[
                                            ["son", "Son kayıt"],
                                            ["giris", "Portal giriş"],
                                            ["hakedis", "Hakediş"],
                                        ].map(([id, lbl]) => (
                                            <button
                                                key={id}
                                                type="button"
                                                onClick={() => setSideTab(id)}
                                                className={`flex-1 text-[9px] py-0.5 rounded ${
                                                    sideTab === id
                                                        ? "bg-white/10 text-slate-200"
                                                        : "text-slate-600 hover:text-slate-400"
                                                }`}
                                            >
                                                {lbl}
                                            </button>
                                        ))}
                                    </div>
                                    <div className="flex-1 overflow-y-auto max-h-[96px] text-[10px] space-y-1">
                                        {sideTab === "son" &&
                                            (data?.son_kayitlar || []).slice(0, 5).map((m) => (
                                                <div key={m.id} className="flex justify-between gap-1">
                                                    <Link
                                                        to={`/e/muvekkiller/${m.id}`}
                                                        className="text-slate-300 truncate hover:text-[#F3E5AB]"
                                                    >
                                                        {m.ad_soyad}
                                                    </Link>
                                                    <span
                                                        className={
                                                            m.vekalet_durumu === "ALINDI"
                                                                ? "text-emerald-400 shrink-0"
                                                                : "text-amber-400 shrink-0"
                                                        }
                                                    >
                                                        {m.vekalet_durumu === "ALINDI" ? "✓" : "—"}
                                                    </span>
                                                </div>
                                            ))}
                                        {sideTab === "giris" &&
                                            (data?.son_portal_girisler || []).slice(0, 5).map((m) => (
                                                <div key={m.id} className="flex justify-between gap-1">
                                                    <span className="text-slate-300 truncate">{m.ad_soyad}</span>
                                                    <span className="text-slate-500 shrink-0 font-mono">
                                                        {fmtDate(m.son_giris)}
                                                    </span>
                                                </div>
                                            ))}
                                        {sideTab === "hakedis" &&
                                            (data?.hakedis_bekleyen_liste || []).slice(0, 5).map((m) => (
                                                <div key={m.id} className="flex justify-between gap-1">
                                                    <Link
                                                        to={`/e/muvekkiller/${m.id}`}
                                                        className="text-slate-300 truncate hover:text-[#F3E5AB]"
                                                    >
                                                        {m.ad_soyad}
                                                    </Link>
                                                    <span className="text-rose-300 shrink-0 font-mono">
                                                        {fmt(m.tutar)}
                                                    </span>
                                                </div>
                                            ))}
                                    </div>
                                </div>
                            </div>

                            {/* İkinci KPI satırı — detayda */}
                            <div className="flex flex-wrap gap-1 mt-2 pt-2 border-t border-white/5">
                                <KpiChip
                                    label="KVKK ✓"
                                    value={data?.kvkk_onayli}
                                    accent="emerald"
                                    title={`%${oranlar.kvkk ?? 0}`}
                                />
                                <KpiChip
                                    label="WhatsApp ✓"
                                    value={data?.whatsapp_onayli}
                                    accent="emerald"
                                    title={`%${oranlar.whatsapp ?? 0}`}
                                />
                                <KpiChip
                                    label="Aracılı"
                                    value={data?.aracili}
                                    accent="violet"
                                    active={isActive("aracili", "1")}
                                    onClick={() => toggleFilter("aracili", "1")}
                                />
                                <KpiChip
                                    label="E-posta —"
                                    value={data?.email_eksik}
                                    accent="amber"
                                    active={isActive("email_eksik", "1")}
                                    onClick={() => toggleFilter("email_eksik", "1")}
                                />
                                <KpiChip
                                    label="Onboarding"
                                    value={data?.onboarding_tamam}
                                    accent="emerald"
                                    active={isActive("onboarding_eksik", "1")}
                                    onClick={() => toggleFilter("onboarding_eksik", "1")}
                                    title={`${data?.onboarding_eksik ?? 0} eksik`}
                                />
                                <KpiChip
                                    label="Aracı ödeme"
                                    value={fmt(data?.toplam_araci_odeme)}
                                    accent="gold"
                                    title={`${data?.hakedis_odenen ?? 0} ödendi`}
                                />
                            </div>
                        </div>
                    ) : null}
                </>
            )}
        </section>
    );
}
